// src/pages/Login.tsx
import { colors } from '@/constants/colors';
import IconLib from '@/utils/IconsLib';
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ButtonSignInSignup from './ButtonSignup';
import GoogleSignInButton from './GoogleSignInButton';
import LoginInput from './LoginInput';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthStore from '@/store/authStore';
import { resolvePostLoginPath } from '@/utils/sellerReturn';
import { signIn, signInStagingTest } from '@/api/auth';

interface Props {
   handleForm: (form: string) => void;
}

interface ApiError {
   message?: string;
   response?: {
      data?: {
         message?: string;
         errors?: string[];
         err?: string;
      };
   };
}

const Login = ({ handleForm }: Props) => {
   const navigate = useNavigate();
   const location = useLocation();
   const { setAuth } = useAuthStore();
   // Prefilled staging tester, so a local dev server does not ask for a password
   // every reload. Google sign-in cannot work here at all — localhost is not a
   // registered origin on the Google client — so email + password is the only
   // way in locally, and retyping it every time is a tax on every UI check.
   //
   // 🔴 GATED ON import.meta.env.DEV, WHICH IS NOT THE SAME AS "staging".
   // Vite replaces this with a literal `false` in ANY `vite build`, so the
   // branch and the password inside it are dropped from the bundle at build
   // time. That means it can never appear on app-staging.smartfi.ph or
   // app.smartfi.ph — both are production builds, and both are on the public
   // internet where a prefilled password would simply be a published one.
   // Do NOT "improve" this by switching to a hostname check.
   const [values, setValues] = useState({
      email: import.meta.env.DEV ? 'staging@smartfi.ph' : '',
      password: import.meta.env.DEV ? '1111111' : '',
   });
   const [error, setError] = useState('');

   // STAGING ONLY (Ray 2026-10-27): "Sign in as Test Buyer" — no password in the page;
   // the staging server signs in its one plain test account (prod answers 404).
   const testSignIn = useMutation({
      mutationFn: signInStagingTest,
      onSuccess: (response) => {
         const { token, user } = response.data;
         if (!user || typeof user.email !== 'string') return void toast.error('Test sign-in failed');
         setAuth(token, user);
         navigate(resolvePostLoginPath(location.state?.from), { replace: true });
      },
      onError: () => toast.error('Test sign-in is not available here.'),
   });

   const { mutate, isPending } = useMutation({
      mutationFn: signIn,
      onSuccess: (response) => {
         const { token, user } = response.data;

         if (
            user &&
            typeof user === 'object' &&
            typeof user.name === 'string' &&
            typeof user.email === 'string'
         ) {
            setAuth(token, user); // ✅ Safe
         } else {
            console.error('Invalid user format:', user);
            toast.error('Login failed: invalid user data');
            return;
         }

         // Honours a pending seller (?next= or the stored note) before the
         // usual dashboard landing — see utils/sellerReturn.
         navigate(resolvePostLoginPath(location.state?.from), { replace: true });
      },

      onError: (err: ApiError) => {
         const errorMessage =
            err.response?.data?.err ||
            err.response?.data?.message ||
            'Login failed. Please try again.';
         setError(errorMessage);
         toast.error(errorMessage);
      },
   });

   const handleSubmit = () => {
      if (isPending) return;

      const { email, password } = values;
      if (!email || !password) {
         setError('Please enter email and password');
         toast.error('Please enter email and password');
         return;
      }

      setError('');
      mutate({ email, password });
   };

   const handleRedirect = (path: string) => {
      // navigate(path);
      handleForm(path);
   };

   return (
      <Wrapper>
         <Bg />
         <Blob $top="8%" $left="10%" $delay="0s" />
         <Blob $top="70%" $left="78%" $delay="3s" $lime />
         <Blob $top="35%" $left="55%" $delay="6s" />
         <Top>
            <BrandRow>
               <BrandBadge>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                     <path d="M5 12.55a11 11 0 0 1 14 0" />
                     <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                     <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                     <circle cx="12" cy="20" r="1.5" fill="white" stroke="none" />
                  </svg>
               </BrandBadge>
               <Logo>SmartFi</Logo>
            </BrandRow>
            <Nav />
         </Top>
         <div className="container">
            <Content>
               <HeadingBlock>
                  <Tagline>WiFi Vendo · Reimagined</Tagline>
                  <Heading>
                     Come in,
                     <br />
                     <HeadingAccent>SmartFi is free.</HeadingAccent>
                  </Heading>
                  <HeroIconHide>
                     <HeroSignal>
                        <HeroRing />
                        <HeroRing $d="0.6s" />
                        <HeroRing $d="1.2s" />
                        <HeroCore>
                           <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                              <path d="M5 12.55a11 11 0 0 1 14 0" />
                              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                              <circle cx="12" cy="20" r="1.5" fill="white" stroke="none" />
                           </svg>
                        </HeroCore>
                     </HeroSignal>
                  </HeroIconHide>
               </HeadingBlock>
               <LoginBox>
                  <Title>Hi! Welcome back.</Title>

                  <CheckList>
                     <CheckItem>
                        <IconLib
                           icon="check-circle-line"
                           size="22px"
                           color="#D2FD58"
                        />
                        <CheckLabel>
                           Manage your WiFi Vendo Vouchers easily online.
                        </CheckLabel>
                     </CheckItem>
                     <CheckItem>
                        <IconLib
                           icon="check-circle-line"
                           size="22px"
                           color="#D2FD58"
                        />
                        <CheckLabel>
                           Let your customers pay online even from their bed.
                        </CheckLabel>
                     </CheckItem>
                  </CheckList>
                  <BtnActionWrapper>
                     <GoogleSignInButton />
                  </BtnActionWrapper>
                  <OrWrapper>
                     <OrLine />
                     <Or>or</Or>
                     <OrLine />
                  </OrWrapper>

                  <LoginActionContent>
                     <LoginInput
                        title="Email"
                        subtitle="Remind me"
                        type="text"
                        hasIcon={true}
                        icon={!values.email ? 'error-warning-fill' : undefined}
                        value={values.email}
                        name="email"
                        onChange={(e) =>
                           setValues({ ...values, email: e.target.value })
                        }
                     />
                     <LoginInput
                        title="Password"
                        subtitle="Forgot"
                        onSubtitleClick={() =>
                           handleRedirect('forgot-password')
                        }
                        type="password"
                        hasIcon={false}
                        value={values.password}
                        name="password"
                        onChange={(e) =>
                           setValues({ ...values, password: e.target.value })
                        }
                     />
                     {error && <ErrorMessage>{error}</ErrorMessage>}
                  </LoginActionContent>

                  <PrimaryCTAWrap onClick={handleSubmit}>
                     <ButtonSignInSignup
                        title="Sign In"
                        isLoading={isPending}
                        color={colors.PRIMARY}
                     />
                  </PrimaryCTAWrap>

                  {IS_STAGING_BUILD && (
                     <TestSignInBtn type="button" disabled={testSignIn.isPending} onClick={() => testSignIn.mutate()}>
                        {testSignIn.isPending ? 'Signing in…' : '🧪 Sign in as Test Buyer (staging)'}
                     </TestSignInBtn>
                  )}

                  <NewHereWrapper>
                     <NewHereLabel>New here?</NewHereLabel>
                     <NewHereLink onClick={() => handleForm('signup')}>
                        Create an account
                     </NewHereLink>
                  </NewHereWrapper>

                  <PolicyWrapper>
                     <PolicyDescription>
                        By continuing, you confirm you are 18 or over and agree
                        to our <PolicyLink href="#">Privacy Policy</PolicyLink>{' '}
                        and <PolicyLink href="#">Terms of Use.</PolicyLink>
                     </PolicyDescription>
                  </PolicyWrapper>
               </LoginBox>
            </Content>
         </div>
      </Wrapper>
   );
};

// Styled Components — SmartFi semi-gaming theme (scoped to Login page)
const bgShift = keyframes`
   0%, 100% { background-position: 0% 50%; }
   50% { background-position: 100% 50%; }
`;
const float = keyframes`
   0%, 100% { transform: translate(0, 0) scale(1); }
   50% { transform: translate(20px, -30px) scale(1.1); }
`;
const expand = keyframes`
   0% { transform: scale(0.55); opacity: 0.9; }
   100% { transform: scale(1.6); opacity: 0; }
`;
const pulse = keyframes`
   0%, 100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 0 0 rgba(210, 253, 88, 0.4); }
   50% { transform: translate(-50%, -50%) scale(1.04); box-shadow: 0 0 0 18px rgba(210, 253, 88, 0); }
`;

const Wrapper = styled.div`
   min-height: 100vh;
   position: relative;
   overflow: hidden;
   color: white;
`;

const Bg = styled.div`
   position: fixed;
   inset: 0;
   background: linear-gradient(135deg, #0b1030 0%, #1a1f4a 35%, #2a2165 70%, #0f1535 100%);
   background-size: 200% 200%;
   animation: ${bgShift} 16s ease infinite;
   z-index: 0;
`;

const Blob = styled.div<{ $top: string; $left: string; $delay: string; $lime?: boolean }>`
   position: fixed;
   top: ${({ $top }) => $top};
   left: ${({ $left }) => $left};
   width: 320px;
   height: 320px;
   border-radius: 50%;
   filter: blur(60px);
   background: ${({ $lime }) =>
      $lime
         ? 'radial-gradient(circle, rgba(210,253,88,0.25) 0%, rgba(210,253,88,0) 70%)'
         : 'radial-gradient(circle, rgba(55,96,249,0.35) 0%, rgba(55,96,249,0) 70%)'};
   animation: ${float} 12s ease-in-out infinite;
   animation-delay: ${({ $delay }) => $delay};
   z-index: 0;
   pointer-events: none;
`;

const Top = styled.div`
   position: relative;
   z-index: 2;
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 14px 1.25rem;
   background: rgba(255, 255, 255, 0.03);
   backdrop-filter: blur(8px);
   border-bottom: 1px solid rgba(255, 255, 255, 0.06);

   @media (max-width: 480px) {
      padding: 12px 1rem;
   }
`;

const BrandRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
`;

const BrandBadge = styled.div`
   width: 34px;
   height: 34px;
   border-radius: 10px;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   display: grid;
   place-items: center;
   box-shadow: 0 4px 14px rgba(55, 96, 249, 0.45);
   svg {
      width: 20px;
      height: 20px;
   }
`;

const Logo = styled.h1`
   font-weight: 800;
   color: white;
   font-size: 22px;
   letter-spacing: 0.02em;
   background: linear-gradient(90deg, #ffffff 0%, #D2FD58 120%);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;

   @media (max-width: 480px) {
      font-size: 1.25rem;
   }
`;

const Nav = styled.nav``;

const NavItem = styled.a`
   color: white;
   
   @media (max-width: 480px) {
      font-size: 0.9rem;
   }
`;

const Content = styled.div`
   position: relative;
   z-index: 1;
   padding: 4rem 1rem 3rem;
   display: grid;
   place-items: center;
   grid-template-columns: 1fr 450px;
   gap: 3rem;

   @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
      padding: 2.5rem 1rem;
   }
   @media (max-width: 480px) {
      padding: 1.5rem 0.75rem;
      gap: 1rem;
   }
`;

const HeadingBlock = styled.div`
   text-align: left;
   @media (max-width: 1024px) {
      text-align: center;
   }
`;

const Tagline = styled.span`
   display: inline-block;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.2em;
   text-transform: uppercase;
   padding: 5px 12px;
   border-radius: 999px;
   background: rgba(210, 253, 88, 0.12);
   color: #D2FD58;
   border: 1px solid rgba(210, 253, 88, 0.35);
   margin-bottom: 18px;
`;

const Heading = styled.h1`
   font-size: 56px;
   font-weight: 800;
   color: white;
   line-height: 1.05;
   letter-spacing: -0.01em;

   @media (max-width: 1024px) {
      font-size: 42px;
   }
   @media (max-width: 768px) {
      font-size: 34px;
   }
   @media (max-width: 480px) {
      font-size: 28px;
   }
`;

const HeadingAccent = styled.span`
   background: linear-gradient(90deg, #D2FD58 0%, #7de8ff 100%);
   -webkit-background-clip: text;
   -webkit-text-fill-color: transparent;
   background-clip: text;
`;

const HeroIconHide = styled.div`
   margin-top: 22px;
   @media (max-width: 1024px) {
      display: none;
   }
`;

const HeroSignal = styled.div`
   position: relative;
   width: 140px;
   height: 140px;
`;

const HeroRing = styled.div<{ $d?: string }>`
   position: absolute;
   inset: 22px;
   border: 2px solid #D2FD58;
   border-radius: 50%;
   opacity: 0;
   animation: ${expand} 1.8s ease-out infinite;
   animation-delay: ${({ $d }) => $d || '0s'};
`;

const HeroCore = styled.div`
   position: absolute;
   top: 50%;
   left: 50%;
   transform: translate(-50%, -50%);
   width: 82px;
   height: 82px;
   border-radius: 50%;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 100%);
   display: grid;
   place-items: center;
   animation: ${pulse} 2s ease-in-out infinite;
   svg {
      width: 28px;
      height: 28px;
   }
`;

const LoginBox = styled.div`
   position: relative;
   padding: 1.75rem;
   width: 450px;
   border-radius: 18px;
   background: linear-gradient(
      160deg,
      rgba(30, 36, 80, 0.72) 0%,
      rgba(20, 24, 56, 0.78) 100%
   );
   backdrop-filter: blur(18px);
   -webkit-backdrop-filter: blur(18px);
   border: 1px solid rgba(255, 255, 255, 0.1);
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45),
      0 0 0 1px rgba(255, 255, 255, 0.04) inset,
      0 0 40px rgba(55, 96, 249, 0.15);
   color: white;
   overflow: hidden;

   /* Neon glow corner accents */
   &::before {
      content: '';
      position: absolute;
      top: -60px;
      right: -60px;
      width: 180px;
      height: 180px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(210, 253, 88, 0.18) 0%, transparent 70%);
      pointer-events: none;
   }
   &::after {
      content: '';
      position: absolute;
      bottom: -80px;
      left: -60px;
      width: 200px;
      height: 200px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(55, 96, 249, 0.22) 0%, transparent 70%);
      pointer-events: none;
   }

   /* Style inner input wrappers (FormContent divs that directly contain an input) */
   & div:has(> input) {
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.04);
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
   }

   /* Input field group labels (title + subtitle above each input) */
   & div:has(> div:has(> input)) > div:first-child > p:first-child {
      color: rgba(255, 255, 255, 0.85);
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.06em;
   }
   & div:has(> div:has(> input)) > div:first-child > p:last-child {
      color: rgba(210, 253, 88, 0.9);
      font-size: 12px;
      text-decoration: none;
      &:hover {
         text-decoration: underline;
      }
   }
   & div:has(> input:focus) {
      border-color: #D2FD58;
      background: rgba(210, 253, 88, 0.04);
      box-shadow: 0 0 0 3px rgba(210, 253, 88, 0.12);
   }

   & input {
      color: white;
      background: transparent;
      caret-color: #D2FD58;
      font-weight: 500;
   }
   & input::placeholder {
      color: rgba(255, 255, 255, 0.35);
   }
   & input:-webkit-autofill,
   & input:-webkit-autofill:hover,
   & input:-webkit-autofill:focus {
      -webkit-text-fill-color: white;
      -webkit-box-shadow: 0 0 0 1000px rgba(30, 36, 80, 0.6) inset;
      transition: background-color 9999s ease-in-out 0s;
   }

   @media (max-width: 768px) {
      width: 100%;
      max-width: 450px;
      padding: 1.25rem;
      border-radius: 16px;
   }
   @media (max-width: 480px) {
      padding: 1rem;
   }
`;

const Title = styled.h2`
   font-size: 26px;
   font-weight: 800;
   text-align: center;
   color: white;
   letter-spacing: -0.01em;

   @media (max-width: 768px) {
      font-size: 22px;
   }
   @media (max-width: 480px) {
      font-size: 19px;
   }
`;

const CheckList = styled.ul`
   margin-top: 2rem;
   
   @media (max-width: 768px) {
      margin-top: 1.5rem;
   }
   
   @media (max-width: 480px) {
      margin-top: 1rem;
   }
`;

const CheckItem = styled.li`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 5px;
   
   @media (max-width: 480px) {
      gap: 6px;
      margin-bottom: 4px;
   }
`;

const CheckLabel = styled.label`
   font-size: 14px;
   color: rgba(255, 255, 255, 0.78);

   @media (max-width: 768px) {
      font-size: 13px;
   }
   @media (max-width: 480px) {
      font-size: 12.5px;
      line-height: 1.4;
   }
`;

const BtnActionWrapper = styled.div`
   margin-top: 2rem;
   
   @media (max-width: 768px) {
      margin-top: 1.5rem;
   }
`;

const OrWrapper = styled.div`
   display: grid;
   grid-template-columns: 1fr auto 1fr;
   align-items: center;
   gap: 10px;
   margin-top: 2rem;
   margin-bottom: 1rem;
   
   @media (max-width: 768px) {
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
   }
`;

const Or = styled.p`
   text-align: center;
   color: rgba(255, 255, 255, 0.55);
   font-size: 12px;
   text-transform: uppercase;
   letter-spacing: 0.2em;

   @media (max-width: 480px) {
      font-size: 11px;
   }
`;

const OrLine = styled.div`
   width: 100%;
   background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.22) 50%,
      rgba(255, 255, 255, 0) 100%
   );
   height: 1px;
`;

const LoginActionContent = styled.div`
   margin-bottom: 2rem;
   
   @media (max-width: 768px) {
      margin-bottom: 1.5rem;
   }
   
   @media (max-width: 480px) {
      margin-bottom: 1rem;
   }
`;

const NewHereWrapper = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 5px;
   margin-top: 2rem;
   
   @media (max-width: 768px) {
      margin-top: 1.5rem;
      flex-direction: column;
      gap: 8px;
   }
   
   @media (max-width: 480px) {
      margin-top: 1rem;
      gap: 6px;
   }
`;

const NewHereLabel = styled.p`
   font-size: 14px;
   color: rgba(255, 255, 255, 0.6);

   @media (max-width: 768px) {
      font-size: 13px;
   }
`;

const NewHereLink = styled.p`
   font-size: 14px;
   color: #D2FD58;
   font-weight: 700;
   cursor: pointer;
   transition: all 0.2s ease;
   &:hover {
      text-decoration: underline;
      filter: brightness(1.1);
   }

   @media (max-width: 768px) {
      font-size: 13px;
   }
`;

const PolicyWrapper = styled.div`
   border-top: 1px solid rgba(255, 255, 255, 0.08);
   padding-top: 1.5rem;
   margin-top: 2rem;
   
   @media (max-width: 768px) {
      padding-top: 1.25rem;
      margin-top: 1.5rem;
   }
   
   @media (max-width: 480px) {
      padding-top: 1rem;
      margin-top: 1rem;
   }
`;

const PolicyDescription = styled.p`
   font-size: 12px;
   color: rgba(255, 255, 255, 0.45);
   line-height: 1.5;
   text-align: center;

   @media (max-width: 480px) {
      font-size: 11.5px;
      line-height: 1.4;
   }
`;

const PolicyLink = styled.a`
   color: rgba(255, 255, 255, 0.75);
   text-decoration: underline;

   &:hover {
      color: #D2FD58;
   }
`;

const PrimaryCTAWrap = styled.div`
   cursor: pointer;
   & > div {
      background: linear-gradient(
         90deg,
         #D2FD58 0%,
         #7de8ff 100%
      ) !important;
      border-radius: 12px;
      box-shadow: 0 10px 26px rgba(210, 253, 88, 0.28),
         0 0 0 1px rgba(210, 253, 88, 0.3) inset;
      transition: filter 0.15s, transform 0.12s;
   }
   & > div p {
      color: #0b1030 !important;
      font-weight: 800 !important;
      letter-spacing: 0.02em;
   }
   &:active > div {
      transform: translateY(1px);
   }
   &:hover > div {
      filter: brightness(1.05);
   }
`;

const ErrorMessage = styled.p`
   color: red;
   font-size: 14px;
   margin-top: 0.5rem;
   text-align: center;
   
   @media (max-width: 768px) {
      font-size: 13px;
   }
   
   @media (max-width: 480px) {
      font-size: 12px;
      margin-top: 0.25rem;
   }
`;

export default Login;

/** Built for the staging server (same test as the dashboard's STAGING badge). */
const IS_STAGING_BUILD = /staging/i.test(import.meta.env.VITE_API_URL || '');

const TestSignInBtn = styled.button`
   width: 100%;
   margin-top: 12px;
   padding: 11px 14px;
   border-radius: 12px;
   border: 1px dashed #f59e0b;
   background: rgba(245, 158, 11, 0.12);
   color: #fbbf24;
   font-weight: 800;
   font-size: 14px;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;
